import {
  Alert,
  Button,
  Card,
  CardBody,
  EmptyState,
  formatDate,
  Money,
  SummaryStat,
  SummaryStats,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useCheck } from '../../api/queries/pay';
import { dollars, LineItemList, LineItemsLoading } from '../lineItems/LineItemList';

/** "Check · 01/06/2023": the page's title. */
export const checkTitle = (payDate: string) => `Check · ${formatDate(payDate)}`;

export interface CheckDetailViewProps {
  /** `YYYY-MM-DD`. */
  payDate: string;
  /** A supervised rep's check; the caller's own when left out. */
  repId?: string;
}

/** A check's detail (pc-dbb.4): the pay date and total, and every line item. No search:
 * Line-item Search finds line items across checks (A16). */
export function CheckDetailView({ payDate, repId }: CheckDetailViewProps) {
  const check = useCheck(payDate, repId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (check.isPending) return <LineItemsLoading dateLabel="Pay date" totalLabel="Total" />;
  if (check.isError) {
    if (check.error instanceof ApiError && check.error.status === 404) {
      return (
        <Card>
          <CardBody>
            <EmptyState
              title="No check on this date"
              description="There’s no pay on this date. Pick a check from Pay History."
              icon="file"
              headingLevel={2}
            />
          </CardBody>
        </Card>
      );
    }
    return (
      <Alert
        tone="danger"
        title="This check couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void check.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }

  return (
    <>
      <SummaryStats aria-label="Check summary">
        <SummaryStat label="Pay date" value={formatDate(check.data.payDate)} size="lg" />
        <SummaryStat
          label="Total"
          value={<Money value={dollars(check.data.amountCents)} />}
          size="lg"
        />
      </SummaryStats>
      <LineItemList
        lineItems={check.data.lineItems}
        totalCents={check.data.amountCents}
        columns={check.data.columns}
        expandedId={expandedId}
        onExpandedChange={setExpandedId}
      />
    </>
  );
}
